<script setup lang="tsx">
import type Container from '~/components/OverflowContainer/Container.vue';
import type { Tab } from '~/components/TabsNavigator';
import type { BreadcrumbItemWithIndex } from '~/components/ui/breadcrumb';
import type { Condominium } from '~/core/entities/condo/Condo';
import type { ApplicationError } from '~~/core/helpers/result';

import { useCondominium } from '@/composables/useCondominium';
import { breakpointsTailwind } from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { useNavigationStore } from '~/store/navigationStore';
import { nuxtCondosRepository } from '~~/adapters/repositories/nuxt/nuxtCondosRepository';
import { CONDO_DETAILS } from '~~/utils/symbols';
import { storeToRefs } from 'pinia';

import { CondoModal } from '~/components/condominium/CondoModal';
import { CondoPriceAndInfo } from '~/components/condominium/CondoPriceAndInfo';
import { CondoTalkToAgentButton } from '~/components/condominium/CondoTalkToAgentButton';
import { CondoTalkToSpecialist } from '~/components/condominium/CondoTalkToSpecialist';
import { DesignAndArchitecture } from '~/components/DesignAndArchitecture';
import { FeaturesGrid } from '~/components/FeaturesGrid';
import { InfoWithIconGrid } from '~/components/InfoWithIconGrid';
import { OverflowContainer } from '~/components/OverflowContainer';
import { CondoCtaCardSection } from '~/components/Sections/CondoCtaCardSection';
import { CondoHeroSection } from '~/components/Sections/CondoHeroSection';
import { CondoPreFooterSection } from '~/components/Sections/CondoPreFooterSection';
import { HighlightedImagesSection } from '~/components/Sections/HighlightedImagesSection';
import { Breadcrumb } from '~/components/ui/breadcrumb';

definePageMeta({
	layout: 'condominium'
});

const { ORIGIN } = useRuntimeConfig().public;
const { condoSlug } = useRoute('condominio-condoSlug').params;

// Fetch condominium data with proper SSR support and error handling
const { data: condominiumData, status: condominiumStatus } = await useAsyncData(
	`condominium-${condoSlug}`,
	async () => {
		const [condominium, error] =
			await nuxtCondosRepository.getCondominiumBySlug(condoSlug);

		if (error) {
			return [null, error];
		}

		return [condominium, null];
	},
	{
		server: true,
		default: () => null
	}
);

const condominium = computed(() => {
	return condominiumData.value?.[0] as Condominium | null;
});

const errorData = computed(() => {
	return condominiumData.value?.[1] as ApplicationError | null;
});

if (errorData.value) {
	const statusCode =
		(errorData.value?.originalErrorObject as any)?.statusCode || 500;

	// Map both 404 (not found) and 400 (bad request) to appropriate error pages
	if (statusCode === 404 || statusCode === 400) {
		throw createError({
			statusCode: statusCode === 400 ? 400 : 404,
			statusMessage: `Condomínio com slug '${condoSlug}' não foi encontrado`,
			fatal: true
		});
	}

	// For other errors, preserve original status code
	throw createError({
		statusCode,
		statusMessage: errorData.value?.message || 'Erro ao carregar condomínio',
		fatal: true
	});
}

const isFetchingCondominium = computed(
	() => condominiumStatus.value === 'pending'
);

// Use composable with fetched data
const {
	condo,
	hasCondoPropertyCharacteristics,
	hasPropertyStatistics,
	breadCrumbItems,
	condoInfoItems,
	condoValuesAndFees,
	propertyCharacteristics,
	designDetails
} = useCondominium(condominium);

useHead({
	meta: [
		{
			property: 'og:url',
			content: `${ORIGIN}/condominio/${condoSlug}`
		}
	]
});

useSeoMeta({
	title: () => `${condo.value?.name} | PilarHomes`,
	description: () =>
		`Conheça todos os detalhes do ${condo.value?.name} localizado em ${condo.value?.address}, ${condo.value?.number}, ${condo.value?.region}, ${condo.value?.city} - ${condo.value?.state}`,
	ogTitle: () => `${condo.value?.name} | PilarHomes`,
	ogDescription: () =>
		`Conheça todos os detalhes do ${condo.value?.name} localizado em ${condo.value?.address}, ${condo.value?.number}, ${condo.value?.region}, ${condo.value?.city} - ${condo.value?.state}`,
	ogImage: () => condo.value?.images[0]?.url
});

const { templateElements } = useCondoContextualMenu();

const { trackBreadcrumbClicked } = useEvents();

const isFixedContactButtonVisible = ref(false);
const contactEl = unref(templateElements.contactEl);
const priceAndInfoRef = useTemplateRef<HTMLElement>('priceAndInfoRef');

useIntersectionObserver(
	templateElements.contactEl,
	([contact]) => {
		isFixedContactButtonVisible.value =
			!contact?.isIntersecting && (contact?.boundingClientRect.top || 0) > 0;
	},
	{ threshold: [0, 1] }
);

function createCondoImageAlt(index: number) {
	return getCondoImageAlt(condo.value?.name || '', index);
}

function handleBreadcrumbClicked(event: BreadcrumbItemWithIndex) {
	trackBreadcrumbClicked({
		[MixpanelProps.CURRENT_PAGE]: 'Condominio Details',
		[MixpanelProps.CTA_TEXT]: event.label,
		[MixpanelProps.DESTINATION_URL]: event.route as unknown as string,
		[MixpanelProps.CLICK_HOME]: event.index === 0,
		[MixpanelProps.CLICK_CITY]: event.index === 1,
		[MixpanelProps.CLICK_NEIGHBORHOOD]: event.index === 2
	});
}

function scrollToElement(element: HTMLElement | null) {
	if (!element) return;

	const elementPosition =
		(element.getBoundingClientRect().top || 0) + window.scrollY - 135;

	window.scrollTo({ top: elementPosition, behavior: 'smooth' });
}

const condoImagesUrls = computed(() => {
	return condo.value?.images.map(image => image.url) || [];
});

const selectedTab = ref<Tab>('images');
const handleOpenModal = (tab: Tab) => {
	selectedTab.value = tab;
	isCondoModalOpen.value = true;
};

const isCondoModalOpen = ref(false);

provide(CONDO_DETAILS, condominium);

const navigationStore = useNavigationStore();
const { isContextualHeaderVisible } = storeToRefs(navigationStore);
</script>

<template>
	<div class="flex size-full flex-col bg-white">
		<CondoModal
			v-if="isCondoModalOpen && condominium?.condo"
			:tab="selectedTab"
			:condominium="condominium"
			@close="isCondoModalOpen = false"
		/>
		<CondoHeroSection
			ref="heroSectionRef"
			:condo-data="condominium"
			:is-loading="isFetchingCondominium"
			@map-clicked="handleOpenModal('map')"
			@photo-clicked="handleOpenModal('images')"
			@video-clicked="handleOpenModal('videos')"
			@address-clicked="scrollToElement(templateElements.locationEl.value)"
		/>

		<Container
			id="overview"
			ref="overviewRef"
			data-section-name="Visão geral"
			class="flex flex-col py-4! lg:pb-0! lg:pt-6"
		>
			<FixedContactButton
				:show="!isCondoModalOpen && isFixedContactButtonVisible"
				:display-after="priceAndInfoRef"
				:hide-after="contactEl"
			>
				<CondoTalkToAgentButton
					section-name="mobile floating cta"
					size="sm"
					variant="primary"
					button-classes="w-full border-none text-sm leading-7"
				/>
			</FixedContactButton>
			<Breadcrumb
				:items="breadCrumbItems"
				@breadcrumb-clicked="handleBreadcrumbClicked"
			/>
		</Container>

		<Container
			class="flex flex-col bg-white pt-4 md:py-11"
			main-class="relative flex w-full flex-col lg:flex-row md:gap-x-8 bg-white"
		>
			<div class="relative w-full pt-4 lg:order-1 lg:w-2/5 lg:pt-0">
				<CondoPriceAndInfo
					ref="priceAndInfoRef"
					:monthly-costs="condominium?.propertyStatistics?.monthlyCosts"
					:price-range="condominium?.propertyStatistics?.priceRange"
					:total-properties="condominium?.propertyStatistics?.totalProperties"
					:agents="condominium?.agents"
					class="top-24 ml-auto md:sticky lg:max-w-[400px]"
					:class="{ 'top-[181px]': isContextualHeaderVisible }"
				>
					<CondoTalkToAgentButton
						section-name="card (property details)"
						variant="primary"
						button-classes="w-full border-none px-4 py-3 text-sm leading-7 md:py-3 md:text-base"
					/>
				</CondoPriceAndInfo>
			</div>

			<div
				class="flex w-full flex-col divide-y divide-white-dark-3 max-lg:pt-8 lg:w-3/5"
			>
				<section
					id="condo-infos"
					class="flex w-full flex-col gap-10 pb-12 text-primary md:gap-11 md:px-0"
				>
					<InfoWithIconGrid
						v-if="hasPropertyStatistics"
						id="condo-values-grid"
						:characteristics="condoValuesAndFees"
						:is-loading="isFetchingCondominium"
						title="Valores e taxas"
						grid-class="gap-y-6 md:gap-y-8 gap-x-5"
					/>

					<InfoWithIconGrid
						v-if="hasCondoPropertyCharacteristics"
						id="condo-properties-features"
						:characteristics="propertyCharacteristics"
						:is-loading="isFetchingCondominium"
						title="Características dos imóveis"
						class="border-b border-white-dark-3 pb-12"
						grid-class="gap-y-6 md:gap-y-8 gap-x-5"
					/>

					<div
						v-if="condominium?.exclusiveProperties?.length"
						id="exclusive-properties"
						ref="exclusivePropertiesRef"
						class="relative -mx-5 border-b border-white-dark-3 pb-10 md:-mx-8 md:pb-12 lg:-mx-10"
						data-section-name="Exclusividades"
					>
						<LazyCondominiumSectionExclusiveProperties
							class="px-5 md:px-8 lg:pl-12 empty:m-0 empty:hidden"
						/>
					</div>

					<div
						v-if="condominium?.propertiesTypologies?.length"
						id="condo-typologies"
						ref="condoTypologiesRef"
						data-section-name="Imóveis"
						class="relative -mx-5 border-b border-white-dark-3 md:-mx-8 lg:-mx-10"
					>
						<LazyCondominiumSectionTypologyCondoTypologySlider
							class="md:px-8 lg:pl-12 empty:m-0 empty:hidden"
						/>
					</div>

					<InfoWithIconGrid
						id="condo-info-grid"
						ref="condoInfoGridRef"
						data-section-name="Condomínio"
						:characteristics="condoInfoItems"
						:is-loading="isFetchingCondominium"
						title="Informações do condomínio"
						grid-class="gap-y-6 gap-x-5"
					/>

					<div id="condo-description" class="w-full">
						<OverflowContainer max-height="max-h-[200px]">
							<div
								class="text-base font-normal leading-[175%] text-primary first-letter:uppercase"
							>
								{{ condominium?.condo.description }}
							</div>
						</OverflowContainer>
					</div>

					<div
						v-if="designDetails.length"
						id="design-architecture"
						class="w-full"
					>
						<DesignAndArchitecture
							:details="designDetails"
							:is-loading="isFetchingCondominium"
						/>
					</div>
				</section>

				<section
					id="characteristics"
					ref="characteristicsRef"
					data-section-name="Características"
					class="flex w-full flex-col gap-11 py-10 text-primary md:px-0 md:py-12"
				>
					<FeaturesGrid
						title="Características do condomínio"
						:features-by-category="condo?.featuresByCategory || {}"
						:is-loading="isFetchingCondominium"
						grid-class="gap-x-5 gap-y-4 grid-cols-1 sm:grid-cols-3"
					/>
					<ClientOnly>
						<HighlightedImagesSection
							:images="condoImagesUrls"
							:images-count="condo?.images.length || 0"
							:is-loading="isFetchingCondominium"
							:image-alt-generator="createCondoImageAlt"
							:necessary-images-count="2"
							@image-clicked="handleOpenModal('images')"
							@see-all-images="handleOpenModal('images')"
						/>
					</ClientOnly>
				</section>

				<SectionsCondoLocationSection
					id="location"
					ref="locationRef"
					data-section-name="Localização"
					:condo="condominium?.condo"
					@cta-clicked="handleOpenModal('map')"
					@cta-nearby-properties-clicked="
						scrollToElement(templateElements.surroundingPropertiesRef.value)
					"
				/>

				<section
					id="contact"
					ref="contactRef"
					class="py-10 md:py-12"
					data-section-name="Atendimento"
				>
					<h2
						class="mb-6 font-matter-sq text-[32px] font-normal leading-[150%] tracking-[-0.64px] text-primary"
					>
						Fale com um especialista
					</h2>

					<CondoTalkToSpecialist
						:agents="condominium?.agents || []"
						:condo-name="condominium?.condo.name || ''"
					>
						<CondoTalkToAgentButton
							section-name="service"
							variant="primary"
							button-classes="h-min w-full border-none px-4 py-3 text-sm md:w-[206px]"
						/>
					</CondoTalkToSpecialist>
				</section>

				<CondoCtaCardSection
					id="sell-card"
					ref="ctaCardRef"
					data-section-name="Anunciar"
					:condo="condominium?.condo"
					class="pt-10 md:pt-12"
				/>
			</div>
		</Container>

		<div ref="surroundingPropertiesRef">
			<LazyCondominiumSectionSurroundingProperties
				class="border-y border-white-dark-3"
			/>
		</div>

		<LazyCondominiumSectionRegionRelatedProperties
			class="border-y border-white-dark-3"
		/>

		<LazyCondominiumSectionSurroundingCondos
			class="border-y border-white-dark-3"
		/>

		<CondoPreFooterSection
			id="pre-footer"
			ref="preFooterRef"
			class="flex size-full flex-row bg-white-dark-1"
		/>
	</div>
</template>
